import { useState } from "react";
import { useAuth, apiClient } from "@/App";
import { Settings as SettingsIcon, User, Building2, Bell, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Settings() {
  const { user, isFounder } = useAuth();
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearType, setClearType] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [isClearing, setIsClearing] = useState(false);

  const handleClearData = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    if (clearType === "everything" && confirmText !== "DELETE EVERYTHING PERMANENTLY") {
      toast.error("Please type the confirmation text exactly");
      return;
    }

    setIsClearing(true);
    try {
      const endpoint = clearType === "everything" 
        ? `/admin/clear-everything?password=${encodeURIComponent(password)}&confirm_text=${encodeURIComponent(confirmText)}`
        : `/admin/clear-data?password=${encodeURIComponent(password)}`;
      
      const response = await apiClient.post(endpoint);
      toast.success(response.data.message);
      setShowClearDialog(false);
      setPassword("");
      setConfirmText("");
      
      if (clearType === "everything") {
        // Logout and redirect since users were recreated
        localStorage.removeItem("token");
        window.location.href = "/";
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear data");
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <div className="page-content" data-testid="settings-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage your account and preferences</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Profile Section */}
        <div className="col-span-12 lg:col-span-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center gap-3 mb-6">
              <User className="w-5 h-5 text-[#FF4500]" />
              <h3 className="text-lg font-semibold text-white">Profile</h3>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#FF4500] flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h4 className="font-semibold text-white">{user?.name}</h4>
                  <p className="text-sm text-gray-400 capitalize">{user?.role?.replace("_", " ")}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-[#262626] space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Email</p>
                  <p className="text-white">{user?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Role</p>
                  <p className="text-white capitalize">{user?.role?.replace("_", " ")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company Info */}
        <div className="col-span-12 lg:col-span-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center gap-3 mb-6">
              <Building2 className="w-5 h-5 text-[#FF4500]" />
              <h3 className="text-lg font-semibold text-white">Company</h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Company Name</p>
                <p className="text-white font-semibold">TAT. ATELIER</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Services</p>
                <p className="text-gray-300">Architecture, Interior Design, Landscape, Project Management</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Established</p>
                <p className="text-gray-300">2021</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Details */}
        <div className="col-span-12 lg:col-span-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center gap-3 mb-6">
              <SettingsIcon className="w-5 h-5 text-[#FF4500]" />
              <h3 className="text-lg font-semibold text-white">Bank Details</h3>
            </div>
            
            <div className="space-y-4 text-sm">
              <div className="p-3 bg-[#171717] rounded border border-[#262626]">
                <p className="text-xs text-gray-500 mb-1">Account Number</p>
                <p className="text-white font-mono">921020022061856</p>
              </div>
              <div className="p-3 bg-[#171717] rounded border border-[#262626]">
                <p className="text-xs text-gray-500 mb-1">IFSC Code</p>
                <p className="text-white font-mono">UTIB0004519</p>
              </div>
              <div className="p-3 bg-[#171717] rounded border border-[#262626]">
                <p className="text-xs text-gray-500 mb-1">Bank Location</p>
                <p className="text-white">KARAPPARAMB, CALICUT</p>
              </div>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="col-span-12 lg:col-span-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-6">
            <div className="flex items-center gap-3 mb-6">
              <Bell className="w-5 h-5 text-[#FF4500]" />
              <h3 className="text-lg font-semibold text-white">Notifications</h3>
            </div>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Email notifications for meetings, site visits, and project updates are enabled.
              </p>
              <div className="p-4 bg-[#171717] rounded border border-[#262626]">
                <p className="text-sm text-[#10B981]">Active</p>
                <p className="text-xs text-gray-500 mt-1">
                  Email notifications via Resend are configured
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Management - Founder Only */}
        {isFounder && (
          <div className="col-span-12">
            <div className="bg-[#1A1A1A] border border-red-900/30 rounded p-6">
              <div className="flex items-center gap-3 mb-6">
                <Trash2 className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-white">Data Management</h3>
                <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">Danger Zone</span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Clear All Data */}
                <div className="p-4 bg-[#171717] rounded border border-[#262626]">
                  <h4 className="font-medium text-white mb-2">Clear All Data</h4>
                  <p className="text-xs text-gray-400 mb-4">
                    Delete all clients, projects, payments, tasks, meetings, and site visits. 
                    Your user account will be preserved.
                  </p>
                  <Button 
                    variant="outline" 
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    onClick={() => { setClearType("data"); setShowClearDialog(true); }}
                    data-testid="clear-data-btn"
                  >
                    Clear All Data
                  </Button>
                </div>

                {/* Clear Everything */}
                <div className="p-4 bg-[#171717] rounded border border-red-900/30">
                  <h4 className="font-medium text-white mb-2">Clear Everything</h4>
                  <p className="text-xs text-gray-400 mb-4">
                    Complete system reset. All data including user accounts will be deleted. 
                    Default accounts will be recreated.
                  </p>
                  <Button 
                    variant="outline" 
                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                    onClick={() => { setClearType("everything"); setShowClearDialog(true); }}
                    data-testid="clear-everything-btn"
                  >
                    Clear Everything
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Clear Data Confirmation Dialog */}
      {showClearDialog && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  {clearType === "everything" ? "Clear Everything" : "Clear All Data"}
                </h3>
                <p className="text-xs text-red-400">This action cannot be undone</p>
              </div>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                {clearType === "everything" 
                  ? "This will delete ALL data including user accounts. You will need to login again with default credentials."
                  : "This will delete all clients, projects, payments, tasks, meetings, and site visits. Your account will be preserved."
                }
              </p>

              <div>
                <label className="block text-xs text-gray-500 mb-1">Enter your password to confirm</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#171717] border border-[#262626] rounded px-3 py-2 text-white text-sm focus:border-[#FF4500] focus:outline-none"
                  placeholder="Your password"
                />
              </div>

              {clearType === "everything" && (
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Type <span className="text-red-400 font-mono">DELETE EVERYTHING PERMANENTLY</span> to confirm
                  </label>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="w-full bg-[#171717] border border-[#262626] rounded px-3 py-2 text-white text-sm focus:border-red-500 focus:outline-none font-mono"
                    placeholder="DELETE EVERYTHING PERMANENTLY"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-[#262626]"
                  onClick={() => { setShowClearDialog(false); setPassword(""); setConfirmText(""); }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleClearData}
                  disabled={isClearing}
                >
                  {isClearing ? "Clearing..." : "Confirm Delete"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
